import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Video } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { videoItems } from "@/data/content";

export default function Videos() {
  const [notice, setNotice] = useState(false);
  const [featured, ...rest] = videoItems;

  return (
    <section id="videos" data-testid="videos-section" className="bg-slate-50 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="05"
          kicker="Vídeos"
          title="Assista e compartilhe"
          description="Espaço reservado para os vídeos oficiais da campanha: apresentação da pré-candidata, propostas e registros das agendas."
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7 }}
            data-testid="video-featured"
            onClick={() => setNotice(true)}
            className="group relative overflow-hidden rounded-3xl text-left shadow-xl focus:outline-none focus:ring-2 focus:ring-brand-orange"
            aria-label={`Assistir: ${featured.title}`}
          >
            <img src={featured.thumb} alt={`Miniatura do vídeo: ${featured.title}`} loading="lazy" className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-orange text-white shadow-2xl shadow-brand-orange/40 transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-1 h-8 w-8" fill="currentColor" />
              </span>
            </div>
            <div className="absolute bottom-0 left-0 p-6 sm:p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-brand-orange">Vídeo em destaque · {featured.duration}</p>
              <h3 className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-white">{featured.title}</h3>
            </div>
          </motion.button>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {rest.map((v, i) => (
              <motion.button
                key={v.title}
                type="button"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.1, duration: 0.55 }}
                data-testid={`video-card-${i}`}
                onClick={() => setNotice(true)}
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-orange"
                aria-label={`Assistir: ${v.title}`}
              >
                <span className="relative h-20 w-32 flex-shrink-0 overflow-hidden rounded-xl">
                  <img src={v.thumb} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute inset-0 flex items-center justify-center bg-brand-navy/40">
                    <Play className="h-6 w-6 text-white" fill="currentColor" />
                  </span>
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-brand-orange">{v.duration}</span>
                  <span className="mt-1 block font-display text-sm font-bold text-slate-900">{v.title}</span>
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {notice && (
          <motion.div
            data-testid="video-notice-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-navy/70 p-4 backdrop-blur-sm"
            onClick={() => setNotice(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              className="relative w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl"
            >
              <button
                type="button"
                data-testid="video-notice-close"
                aria-label="Fechar"
                onClick={() => setNotice(false)}
                className="absolute right-4 top-4 rounded-full p-2 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange/15 text-brand-orange">
                <Video className="h-7 w-7" />
              </div>
              <h3 className="font-display text-xl font-extrabold text-slate-900">Vídeo em breve</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Este espaço está reservado para o material audiovisual oficial da campanha — os vídeos
                serão publicados aqui em breve.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
